// @flow
import * as React from 'react';

import { Outlet, Route } from 'react-router-dom';
import { compose } from 'redux';

import type { ExtraRouteProps } from '@gandi/app-runner';
import { TrackingProvider } from '@gandi/tracking';
import { provideTranslate } from '@gandi/translate';

import { provideAssetPath } from 'gandi.react-asset-path';

import CartConfigure from './Cart';
import Account from './Cart/Account';
import AccountChoice from './Cart/AccountChoice';
import { AccountCreate } from './Cart/AccountCreate';
import AuthInfo from './Cart/AuthInfo';
import Claims from './Cart/Claims';
import DomainContactEdition from './Cart/DomainContacts/DomainContactEdition';
import DomainContactsWizard from './Cart/DomainContacts/Wizard';
import SetupOverview from './Cart/Setup/Overview';
import SetupRoleContact from './Cart/Setup/RoleContact';
import Smd from './Cart/Smd';
import Templates from './Cart/Templates';

import CheckoutIndex from './Checkout';
import AwaitingPayment from './Checkout/AwaitingPayment';
import Checkout from './Checkout/Checkout';
import GenerateProforma from './Checkout/GenerateProforma';
import PaymentReturnWrapper from './Checkout/PaymentReturn';
import PaymentReturnBase from './Checkout/PaymentReturnBase';

import Cloud from './Cloud';
import CloudExtend from './Cloud/Extend';

import DomainCreate from './DomainCreate';
import DomainRenew from './DomainRenew';
import DomainTransfer from './DomainTransfer';
import DomainTransferBulkResult from './DomainTransferBulkResult';
import FakeOverview from './FakeOverview';
import MailboxCreate from './MailboxCreate';
import { MailboxStandaloneCreate } from './MailboxStandaloneCreate';
import OverviewRoot from './Overview';
import Overview from './Overview/components/Overview';
import SimpleHosting from './SimpleHosting';
import SimpleHostingCreate from './SimpleHosting/Create';
import SimpleHostingRenew from './SimpleHosting/Renew';

import SimpleHostingOneClickRedirect from './SimpleHosting/components/SimpleHostingOneClickRedirect';

import StudioAccount from './Studio/Account';
import StudioConvert from './Studio/Convert';

import TmchLayout from './Tmch';
import TmchForm from './Tmch/Form';
import TmchOverview from './Tmch/Overview';
import WebHosting from './WebHosting';
import {
  CreateCertificate,
  CreateSHSCertificate,
  RenewCertificate,
} from './certificate';
import { ACMECertificate } from './certificate/ACMECertificate';
import { CertificateLayout } from './certificate/CertificateLayout';
import EnsureSSRLoggedInCartOwner from './components/EnsureSSRLoggedInCartOwner';
import NotFound from './components/NotFound';
import { QueryPersister } from './components/QueryPersister';
import Root from './components/Root';
import RootWithPreFetch from './components/RootWithPreFetch';
import { Redirect } from './helpers/router';

export const routes: React.MixedElement = (
  <>
    <Route index element={<Redirect to="/domain/suggest" />} />

    <Route element={<RootWithPreFetch showCartTray />}>
      <Route
        path="simplehosting"
        element={<SimpleHosting />}
        handle={{
          tracking: { chapter2: 'Hosting', chapter3: 'Simple_hosting' },
        }}
      >
        <Route
          path="legacy/:cartId?/:productId?"
          element={<SimpleHostingCreate />}
          handle={{ tracking: { name: 'legacy_create' } }}
        />
        {/* old Redirect */}
        <Route
          path="one-click/:cartId?/:productId?"
          element={<SimpleHostingOneClickRedirect />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="renew/:identifier/:cartId?"
          element={<SimpleHostingRenew />}
          handle={{ tracking: { name: 'legacy_renew' } }}
        />
      </Route>

      <Route
        path="domain"
        handle={{
          tracking: {
            chapter2: 'Domain',
          },
        }}
      >
        <Route
          index
          element={<Redirect to="suggest" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="suggest/:cartId?"
          element={<DomainCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
        <Route
          path="renew/:cartId?"
          element={<DomainRenew />}
          handle={{ tracking: { name: 'renew' } }}
        />
        <Route
          path="transfer/bulk-result/:cartId?"
          element={<DomainTransferBulkResult />}
          handle={{ tracking: { chapter3: 'Transfer', name: 'bulk-result' } }}
        />
      </Route>

      <Route
        path=":fqdn/mailboxes"
        handle={{ tracking: { chapter2: 'Mailboxes' } }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<MailboxCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
      </Route>

      <Route
        path="cloud"
        element={<Cloud />}
        handle={{ tracking: { chapter2: 'Hosting', chapter3: 'Cloud' } }}
      >
        <Route
          index
          element={<Redirect to="extend" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="extend/:cartId?"
          element={<CloudExtend />}
          handle={{ tracking: { name: 'extend' } }}
        />
      </Route>
    </Route>
    <Route element={<RootWithPreFetch />}>
      <Route path="cart" handle={{ tracking: { chapter2: 'Cart' } }}>
        <Route
          index
          element={<FakeOverview />}
          handle={{ tracking: { noTrack: true } }}
        />

        <Route element={<EnsureSSRLoggedInCartOwner />}>
          <Route path=":cartId">
            <Route
              index
              element={<Redirect to="overview" />}
              handle={{ tracking: { noTrack: true } }}
            />

            <Route element={<OverviewRoot />}>
              <Route
                path="overview/:orderId?"
                element={<Overview />}
                handle={{ tracking: { chapter3: 'Overview', name: 'default' } }}
              />
            </Route>

            <Route
              path="configure"
              element={<CartConfigure />}
              handle={{ tracking: { chapter3: 'Configure' } }}
            >
              <Route
                index
                element={<Redirect to="account-choice" />}
                handle={{ tracking: { name: 'configure_redir' } }}
              />
              <Route
                path="account-choice"
                handle={{
                  tracking: {
                    chapter3: 'Account_choice',
                  },
                }}
              >
                <Route
                  index
                  handle={{
                    tracking: {
                      name: 'default',
                    },
                  }}
                  element={<AccountChoice />}
                />
              </Route>
              <Route
                path="account"
                element={<Account />}
                handle={{ tracking: { chapter3: 'Account_choice' } }}
              >
                <Route
                  index
                  element={<Redirect to=".." />}
                  handle={{ tracking: { name: 'orga_old_redir' } }}
                />
                <Route
                  path="create"
                  element={<AccountCreate />}
                  handle={{ tracking: { name: 'orga_create' } }}
                />
                <Route
                  path="*"
                  element={<Redirect to="../create" />}
                  handle={{ tracking: { name: 'orga_old' } }}
                />
              </Route>
              <Route
                path="domain-contacts"
                element={<DomainContactsWizard />}
              />
              <Route path=":productId/authinfo" element={<AuthInfo />} />
              <Route path=":productId/claims" element={<Claims />} />
              <Route path=":productId/smd" element={<Smd />} />
              <Route
                path=":productId/contacts/:role"
                handle={{ tracking: { name: 'contact_role' } }}
                element={<DomainContactEdition />}
              />
              <Route
                path="templates"
                element={<Templates />}
                handle={{ tracking: { name: 'templates' } }}
              />
              <Route path="setup" handle={{ tracking: { chapter3: 'Setup' } }}>
                <Route
                  index
                  element={<Redirect to="overview" />}
                  handle={{ tracking: { noTrack: true } }}
                />
                <Route path="overview/:process?" element={<SetupOverview />} />
                <Route
                  path="role-contact/:role/:process"
                  element={<SetupRoleContact />}
                  handle={{ tracking: { name: 'role-contact' } }}
                />
              </Route>
            </Route>

            <Route
              element={<CheckoutIndex />}
              handle={{ tracking: { chapter3: 'Checkout' } }}
            >
              <Route
                path="checkout/:orderId"
                element={<Checkout />}
                handle={{ tracking: { name: 'default' } }}
              />
              <Route
                path="generate-proforma"
                element={<GenerateProforma />}
                handle={{
                  tracking: { name: 'generate-proforma' },
                }}
              />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route
        path="tmch"
        element={<TmchLayout />}
        handle={{ tracking: { chapter2: 'SMEs', chapter3: 'Tmch' } }}
      >
        <Route index element={<TmchOverview />} />
        <Route
          path="tmch-registration"
          element={<TmchForm />}
          handle={{ tracking: { name: 'registration' } }}
        />
      </Route>

      <Route
        path="certificate"
        handle={{ tracking: { chapter2: 'Certificate' } }}
        // RR6: migrate with `useCallbackPrompt`
        element={<CertificateLayout />}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<CreateCertificate />}
          handle={{ tracking: { name: 'create' } }}
        />
        <Route
          path="renew/:certUUID/:cartId?"
          element={<RenewCertificate />}
          handle={{ tracking: { name: 'renew' } }}
        />
        <Route
          // improvement: `shs` can become a `:context` to be able to buy a cert based on a cn
          path="shs/create/:cn/:cartId?"
          element={<CreateSHSCertificate />}
          handle={{ tracking: { name: 'shs-create' } }}
        />
      </Route>
    </Route>
    <Route element={<RootWithPreFetch showCartTray backgroundColor="white" />}>
      <Route
        path="mailboxes"
        handle={{ tracking: { chapter2: 'Mailboxes-standalone' } }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?"
          element={<MailboxStandaloneCreate />}
          handle={{ tracking: { name: 'create' } }}
        />
      </Route>
      <Route
        path="domain"
        handle={{
          tracking: {
            chapter2: 'Domain',
          },
        }}
      >
        <Route
          path="transfer/:cartId?"
          element={<DomainTransfer />}
          handle={{ tracking: { name: 'transfer' } }}
        />
      </Route>
    </Route>
    <Route element={<RootWithPreFetch backgroundColor="white" />}>
      <Route
        path="certificate/acme/:cartId?"
        element={<ACMECertificate />}
        handle={{
          tracking: {
            chapter2: 'Certificate',
            chapter3: 'ACME',
            name: 'create',
          },
        }}
      />
      <Route
        path="simplehosting"
        element={<SimpleHosting />}
        handle={{
          tracking: { chapter2: 'Hosting', chapter3: 'Simple_hosting' },
        }}
      >
        <Route
          index
          element={<Redirect to="create" />}
          handle={{ tracking: { noTrack: true } }}
        />
        <Route
          path="create/:cartId?/:productId?"
          element={<WebHosting />}
          handle={{ tracking: { name: 'create' } }}
        />
      </Route>
      <Route path="studio">
        <Route
          path=":vhost/convert/:cartId?"
          element={<StudioConvert />}
          handle={{
            tracking: {
              chapter2: 'Studio',
              name: 'convert',
            },
          }}
        />
        <Route
          path=":cartId/account"
          element={<StudioAccount />}
          handle={{ tracking: { chapter2: 'Studio', name: 'account' } }}
        />
      </Route>
    </Route>

    <Route
      element={<PaymentReturnWrapper />}
      handle={{ tracking: { chapter2: 'Shipping_order' } }}
    >
      <Route path="payment-return" element={<PaymentReturnBase />} />
    </Route>
    <Route
      path="awaiting-payment/:orderId"
      element={<AwaitingPayment />}
      handle={{
        tracking: {
          chapter2: 'Cart',
          chapter3: 'Checkout',
          name: 'awaiting-payment',
        },
      }}
    />

    <Route
      path="*"
      element={<NotFound />}
      handle={{ tracking: { chapter2: 'Not_found' } }}
    />
  </>
);

export default (
  args: ExtraRouteProps,
): ExactReactElement_DEPRECATED<typeof Route> => {
  const DecoratedApp = compose(
    provideTranslate(args.translator),
    provideAssetPath(
      args.assetEndpoint,
      args.manifestContent,
      args.assetPrefix,
    ),
  )(Root);

  return (
    <Route
      element={
        <TrackingProvider>
          <Outlet />
        </TrackingProvider>
      }
    >
      <Route element={<QueryPersister />}>
        <Route
          path="/"
          element={<DecoratedApp />}
          handle={{ tracking: { chapter1: 'Shop' } }}
        >
          {routes}
        </Route>
      </Route>
    </Route>
  );
};
